import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CreditCardIcon,
} from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS } from '@/config';
import { Notification as NotificationType } from '@/stores/notifications';

import { Button } from '../Elements';

export const notificationCollection = {
  info: {
    icon: <InformationCircleIcon className="h-6 w-6" aria-hidden="true" />,
    style: 'bg-blue-100 text-blue-900 border-blue-200',
  },
  success: {
    icon: <CheckCircleIcon className="h-6 w-6" aria-hidden="true" />,
    style: 'bg-green-100 text-green-900 border-green-200',
  },
  warning: {
    icon: <ExclamationCircleIcon className="h-6 w-6" aria-hidden="true" />,
    style: 'bg-yellow-100 text-yellow-900 border-yellow-200',
  },
  error: {
    icon: <XCircleIcon className="h-6 w-6" aria-hidden="true" />,
    style: 'bg-red-100 text-red-900 border-red-200',
  },
  cardReminder: {
    icon: <CreditCardIcon className="h-6 w-6" aria-hidden="true" />,
    style: 'bg-yellow-100 text-yellow-900 border-yellow-200',
  },
};

export type NotificationProps = {
  notification: NotificationType;
  onDismiss: (id: string) => void;
};

export const Notification = ({
  notification: {
    id = nanoid(),
    type,
    title,
    message,
    customIcon,
    closeable = true,
    hideOnDeclare = false,
    autoClose,
    URLRedirectOnClose,
    progressBarPlacement = 'bottom',
    accent,
  },
  onDismiss,
}: NotificationProps) => {
  const { t } = useTranslation();
  const notificationEle = useRef<HTMLInputElement>(null);
  const ariaLabel = typeof title === 'string' ? type : t('common.notification') || '';
  const notificationIcon = customIcon || notificationCollection[type].icon;
  const [isVisible, setIsVisible] = useState(!hideOnDeclare);

  const onDefaultDismiss = (id: string) => {
    onDismiss(id);
  };

  const hideNotification = () => {
    setIsVisible(false);
  };

  const progressBar = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoClose) {
      const element = progressBar.current;
      let width = 100;

      const scene = () => {
        if (width <= 0) {
          clearInterval(identity);
          setIsVisible(false);
          URLRedirectOnClose && window.location.assign(URLRedirectOnClose);
        } else {
          width--;
          element && (element.style.width = `${width}%`);
        }
      };

      const identity = setInterval(
        scene,
        (autoClose || NOTIFICATION_DEFAULT_AUTOCLOSE_TIME_IN_MS) / 100
      );

      return () => clearInterval(identity);
    }
  }, [autoClose, URLRedirectOnClose]);

  return (
    <div
      ref={notificationEle}
      id={id}
      className={clsx('w-full lg:w-1/3 flex flex-col items-center space-y-4 sm:items-end')}
    >
      <Transition
        appear={true}
        show={isVisible}
        as={Fragment}
        enter="transform ease-out duration-800 transition"
        enterFrom="-translate-y-10 opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transition ease-in duration-800"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="-translate-y-10 opacity-0"
        afterLeave={() => closeable && onDefaultDismiss(id)}
      >
        <div
          className={clsx(
            'w-full shadow-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden',
            notificationCollection[type].style,
            {
              'border-t-5': accent === 'top',
              'border-r-5': accent === 'right',
              'border-b-5': accent === 'bottom',
              'border-l-5': accent === 'left',
            }
          )}
        >
          <div className="p-4 relative" role="alert" aria-label={ariaLabel}>
            <div className="flex items-start">
              <div className="flex-shrink-0">{notificationIcon}</div>
              <div className="ml-3 w-0 flex-1 pt-0.5">
                {title ? <p className="text-sm font-medium">{title}</p> : ''}
                <p className={clsx('text-sm', { 'mt-1': title })}>{message}</p>
              </div>
              <div className="ml-4 flex-shrink-0 flex">
                <Button
                  onClick={hideNotification}
                  variant="raw"
                  size="free"
                  className="bg-transparent inline-flex opacity-75 hover:opacity-100 focus:outline-none"
                >
                  <span className="sr-only">{t('common.close')}</span>
                  <XIcon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </div>
            </div>
            {autoClose && (
              <div
                ref={progressBar}
                className={clsx('absolute bottom-0 left-0 right-0 bg-black/20 h-1', {
                  'bottom-0': progressBarPlacement === 'bottom',
                  'top-0': progressBarPlacement === 'top',
                })}
              />
            )}
          </div>
        </div>
      </Transition>
    </div>
  );
};
